import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import "./noticeboard.scss";
import { useEffect, useState } from "react";
import { noticeService } from "../../firebase/services";
import { useAuth } from "../../context/AuthContext";

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "maintenance", label: "Maintenance Notices" },
  { value: "events", label: "Events" },
  { value: "community", label: "Community Alerts" },
  { value: "policy", label: "Policy/Rules Updates" },
];

const NoticeBoard = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ title: "", message: "", category: "maintenance", expiryDate: "" });
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  // Search/filter state
  const [searchCategory, setSearchCategory] = useState("all");
  const [searchDate, setSearchDate] = useState("");
  const [searchText, setSearchText] = useState("");

  const [editId, setEditId] = useState(null);

  const fetchNotices = async () => {
    setLoading(true);
    const res = await noticeService.getAllNotices();
    if (res.success) {
      setNotices(res.data);
      setError(null);
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNotices();
    // Optionally, set up polling or real-time updates here
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEdit = (notice) => {
    setForm({
      title: notice.title,
      message: notice.message,
      category: notice.category || "maintenance",
      expiryDate: notice.expiryDate || ""
    });
    setEditId(notice.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this notice?")) {
      const res = await noticeService.deleteNotice(id);
      if (res.success) {
        fetchNotices();
      } else {
        alert("Failed to delete notice: " + res.error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim() || !form.category) return;
    setSubmitting(true);
    let res;
    if (editId) {
      res = await noticeService.updateNotice(editId, {
        title: form.title,
        message: form.message,
        category: form.category,
        expiryDate: form.expiryDate || null,
      });
    } else {
      res = await noticeService.addNotice({
        title: form.title,
        message: form.message,
        category: form.category,
        expiryDate: form.expiryDate || null,
        createdBy: user?.uid || null
      });
    }
    setSubmitting(false);
    if (res.success) {
      setForm({ title: "", message: "", category: "maintenance", expiryDate: "" });
      setEditId(null);
      fetchNotices();
    } else {
      alert((editId ? "Failed to update notice: " : "Failed to add notice: ") + res.error.message);
    }
  };

  const handleCancelEdit = () => {
    setForm({ title: "", message: "", category: "maintenance", expiryDate: "" });
    setEditId(null);
  };

  // Filtering logic
  const filteredNotices = notices.filter((notice) => {
    // Category filter
    if (searchCategory !== "all" && notice.category !== searchCategory) return false;
    // Date filter (YYYY-MM-DD) in Manila time
    if (searchDate && notice.createdAt?.seconds) {
      const utcDate = new Date(notice.createdAt.seconds * 1000);
      const manilaDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
      const noticeDateManila = manilaDate.getFullYear() + '-' +
        String(manilaDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(manilaDate.getDate()).padStart(2, '0');
      if (noticeDateManila !== searchDate) return false;
    }
    // Text search (title/message)
    if (searchText) {
      const text = searchText.toLowerCase();
      if (!notice.title?.toLowerCase().includes(text) && !notice.message?.toLowerCase().includes(text)) return false;
    }
    return true;
  });

  // Helper: check if a notice is archived (older than 7 days in Manila time or after expiryDate)
  const isArchived = (notice) => {
    if (!notice.createdAt?.seconds) return false;
    const utcDate = new Date(notice.createdAt.seconds * 1000);
    const manilaDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
    // If expiryDate is set, archive after that date
    if (notice.expiryDate) {
      // expiryDate is YYYY-MM-DD, treat as Manila time midnight
      const [year, month, day] = notice.expiryDate.split('-').map(Number);
      const expiry = new Date(Date.UTC(year, month - 1, day));
      // Compare only date part
      if (now >= expiry) return true;
      return false;
    }
    // Otherwise, archive after 7 days
    const diffMs = now - manilaDate;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays > 7;
  };

  return (
    <div className="noticeboard">
      <Sidebar />
      <div className="noticeboardContainer">
        <Navbar />
        <div className="noticeboardContent">
          <h2 className="noticeboardTitle">Notice Board</h2>
          <p className="noticeboardSubtitle">Announcements for all tenants</p>

          {/* Search/Filter Bar */}
          <div className="noticeSearchBar">
            <select
              value={searchCategory}
              onChange={e => setSearchCategory(e.target.value)}
              className="noticeSearchCategory"
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={searchDate}
              onChange={e => setSearchDate(e.target.value)}
              className="noticeSearchDate"
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="noticeSearchText"
            />
            <button
              type="button"
              onClick={() => { setSearchCategory("all"); setSearchDate(""); setSearchText(""); }}
              className="noticeSearchClear"
            >Clear</button>
          </div>

          {user?.role === "admin" && (
            <form className="noticeForm" onSubmit={handleSubmit}>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                required
                className="noticeFormCategory"
              >
                {CATEGORIES.filter(cat => cat.value !== "all").map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <input
                type="text"
                name="title"
                placeholder="Notice Title"
                value={form.title}
                onChange={handleChange}
                maxLength={80}
                required
              />
              <textarea
                name="message"
                placeholder="Notice Message"
                value={form.message}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                required
              />
              <input
                type="date"
                name="expiryDate"
                value={form.expiryDate}
                onChange={handleChange}
                className="noticeFormExpiryDate"
                min={new Date().toISOString().slice(0, 10)}
                placeholder="Expiry Date (optional)"
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                {editId && (
                  <button type="button" onClick={handleCancelEdit} style={{ background: '#aaa', color: '#fff' }}>
                    Cancel
                  </button>
                )}
                <button type="submit" disabled={submitting}>
                  {submitting ? (editId ? "Updating..." : "Posting...") : (editId ? "Update Notice" : "Post Notice")}
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="noticeboardLoading">Loading notices...</div>
          ) : error ? (
            <div className="noticeboardError">{error}</div>
          ) : filteredNotices.length === 0 ? (
            <div className="noticeboardEmpty">No notices found.</div>
          ) : (
            <div className="noticeList">
              {filteredNotices.map((notice) => (
                <div className="noticeCard" key={notice.id}>
                  <div className="noticeCardHeader">
                    <span className="noticeCardTitle">{notice.title}</span>
                    <span className="noticeCardCategory">{CATEGORIES.find(cat => cat.value === notice.category)?.label || notice.category}</span>
                    <span className="noticeCardDate">
                      {notice.createdAt?.seconds
                        ? (() => {
                            const utcDate = new Date(notice.createdAt.seconds * 1000);
                            return utcDate.toLocaleString('en-US', {
                              timeZone: 'Asia/Manila',
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          })()
                        : ""}
                    </span>
                    {user?.role === "admin" && notice.expiryDate && (
                      <span className="noticeCardExpiry">Expiry: {notice.expiryDate}</span>
                    )}
                    {isArchived(notice) && (
                      <span className="noticeCardArchived">Archived</span>
                    )}
                  </div>
                  <div className="noticeCardMessage">{notice.message}</div>
                  {user?.role === "admin" && (
                    <div className="noticeCardActions">
                      <button className="noticeEditBtn" onClick={() => handleEdit(notice)}>Edit</button>
                      <button className="noticeDeleteBtn" onClick={() => handleDelete(notice.id)}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoticeBoard; 